import { useEffect, useId, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Modal } from "react-bootstrap";
import "./Documents.css";
import "./DocumentModal.css";
import "./DocumentPopup.css";
import { Document, Page } from "react-pdf";
import { useDebounce } from "use-debounce";

import { getClientId, getCaseId, getToken } from "../../Utils/helper";
import { PDF_LOAD_OPTIONS } from "../../Utils/pdfLoadOptions";
import { usePdfPageWindow } from "../../Utils/usePdfPageWindow";
import api, { api_without_cancellation } from "../../api/api";
import { useDocumentModal } from "../common/CustomModal/CustomModalContext";
import { useChat } from "../AChatWindow/hooks/useChat";
import { setSelectedUser } from "../../Redux/new-chat-store/chatSlice";
import ChatWindow from "../AChatWindow/components/ChatWindow";

import DocumentModalSideBar from "./DocumentSideBar/DocumentModalSideBar";
import { PdfThumbnailRail, PdfThumbnailPages } from "./PdfThumbnail";
import ClientSearch, { DocumentReviewTo } from "./ClientSearch";
import PdfNavigation from "./PdfNavigation";
import DeleteConfirmationModal from "./DocumentSideBar/DeleteConfimationModal";
import "../../Utils/pdfWorker";

const pdfPageStyle = {
  position: "relative",
  marginBottom: "5px",
  marginTop: "5px",
  marginLeft: "auto",
  marginRight: "auto",
  boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.09)",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  display: "flex",
};

const DocumentModal = () => {
  const documentSearchId = useId();
  const [numPages, setNumPages] = useState(null);
  const [updatedUser, setUpdatedUser] = useState({});
  const [chatDocumentThread, setChatDocumentThread] = useState(false);
  const {
    documentURL,
    isVisible,
    hideDocumentModal,
    documentData,
    refetchDocumentData,
    initialSearchTerm,
  } = useDocumentModal();
  const {
    firms,
    selectedFirm,
    users,
    loading: chatLoading,
    error: chatError,
    handleSelectFirm,
    sendMessage,
    searchUsers,
    messages,
    typingUsers,
    handleTyping,
    markAsRead,
    loadMoreMessages,
    handleSelectUser,
  } = useChat({ skipInitialization: true });
  // const url = new URL(documentURL);
  const cleanUrl = (url) => {
    if (!url) return url;

    // check if the path (before ?) ends with .pdf
    const [base] = url.split("?");
    return base.toLowerCase().endsWith(".pdf") ? base : url;
  };
  const source = "";
  const threadId = "";
  // if (documentURL !== "") {
  //   const urlObj = new URL(documentURL);
  //   const params = new URLSearchParams(urlObj.search);
  //   // eslint-disable-next-line unused-imports/no-unused-vars
  //   threadId = params?.get("thread_id");
  //   source = params?.get("source");
  // }
  useEffect(() => {
    if (!users || users.length === 0 || documentURL === "") return; // wait until users arrive
    const urlObj = new URL(documentURL);
    const params = new URLSearchParams(urlObj.search);
    const threadId = params.get("thread_id");

    const selectedUser = users.find((u) => u.id === threadId);
    if (!selectedUser) return;

    const updatedUser = {
      ...selectedUser,
      threadId,
    };

    setSelectedUser(selectedUser);
    setUpdatedUser(updatedUser);
    setChatDocumentThread(false);
  }, [users, threadId]);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  // Real size of every page that has rendered at least once, so placeholders
  // for pages not yet rendered are the right height and nothing shifts.
  const [pageSizes, setPageSizes] = useState({});
  // Whether some page has already set the baseline size and fit zoom.
  const baselineSized = useRef(false);
  const [containerRef, setContainerRef] = useState(null);
  // The rail's scroll container (observer root) and the element its thumbnails
  // are portaled into. Callback refs as state, matching containerRef below.
  const [railScrollEl, setRailScrollEl] = useState(null);
  const [railPortalEl, setRailPortalEl] = useState(null);
  const [searchString, setSearchString] = useState("");
  // Wait until the user pauses before starting PDF extraction/API work. A
  // short 150ms delay fired repeatedly during normal typing on large files.
  const [debouncedSearchString] = useDebounce(searchString, 500);
  const [ocrGeometryByPage, setOcrGeometryByPage] = useState({});
  const [ocrSearchPages, setOcrSearchPages] = useState([]);
  const [ocrSearchPagination, setOcrSearchPagination] = useState({
    truncated: false,
    nextAfterPage: null,
    isLoadingMore: false,
  });
  const [completedSearchQuery, setCompletedSearchQuery] = useState("");
  const [searchProgress, setSearchProgress] = useState({
    done: 0,
    total: 0,
    isSearching: false,
  });
  const fetchingOcrPages = useRef(new Set());
  const highlightMeasureContext = useRef(null);
  const activeGeometryDocumentId = useRef(null);
  // const [showSideBar, setShowSideBar] = useState(true);
  const showSideBar = true; // setter was not being used
  const observer = useRef();
  const pageVisibility = useRef(new Map());
  // const pages = useSelector((state) => state.caseData?.pages);
  const currentCaseId = getCaseId();
  const [slotsData, setSlotsData] = useState([]);
  const [defaultZoom, setdefaultZoom] = useState("fit");
  const token = getToken();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isVisible) setSearchString(initialSearchTerm || "");
  }, [documentURL, initialSearchTerm, isVisible]);

  const [pages, setPages] = useState([]);

  const handleHideDocumentModal = () => {
    setChatDocumentThread(false);
    setSearchString("");
    setPage(1);
    hideDocumentModal();
  };

  const onZoomChange = (zoomType) => {
    const containerWidth = containerRef?.offsetWidth || 0;
    const containerHeight = containerRef?.offsetHeight || 0;
    switch (zoomType) {
      case "in":
        setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 4));
        setdefaultZoom("");
        break;
      case "out":
        setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
        setdefaultZoom("");
        break;
      case "width": {
        const widthScale = containerWidth / pageDimensions.width;
        const roundedWidthScale = Math.floor(widthScale * 10) / 10 - 0.1;
        setZoomLevel(roundedWidthScale);
        setdefaultZoom("");
        break;
      }
      case "height": {
        const heightScale = containerHeight / pageDimensions.height;
        setZoomLevel(heightScale);
        setdefaultZoom("");
        break;
      }
      case "fit": {
        const fitScale = Math.min(
          containerWidth / pageDimensions.width,
          containerHeight / pageDimensions.height
        );
        setZoomLevel(Math.max(fitScale - 0.05, 0.1));
        setdefaultZoom("fit");
        break;
      }
      default:
        if (typeof zoomType === "number") {
          setZoomLevel(zoomType);
          setdefaultZoom("");
        }
        break;
    }
  };

  async function onDocumentLoadSuccess(pdf) {
    setNumPages(pdf?.numPages || 0);
    setError("");
  }

  // Drop everything derived from the previous document so a stale proxy is
  // never searched and stale sizes never size the new document's placeholders.
  useEffect(() => {
    setNumPages(null);
    setPageSizes({});
    setPageDimensions({ width: 0, height: 0 });
    setOcrGeometryByPage({});
    setOcrSearchPages([]);
    setOcrSearchPagination({ truncated: false, nextAfterPage: null, isLoadingMore: false });
    setCompletedSearchQuery("");
    setSearchProgress({ done: 0, total: 0, isSearching: false });
    fetchingOcrPages.current.clear();
    pageVisibility.current.clear();
    baselineSized.current = false;
  }, [documentURL]);

  function scrollPageToView(pageNumber, updatePageNumber = (pageNumber) => pageNumber) {
    const pageElementId = `pdf_page_${pageNumber}`;
    const pageElement = document.getElementById(pageElementId);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: "smooth" });
      updatePageNumber(pageNumber);
    }
  }
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef || !pageDimensions.width || !pageDimensions.height) return;

      if (pageDimensions.width && pageDimensions.height) {
        const containerWidth = containerRef.offsetWidth;
        if (defaultZoom === "fit") {
          handleZoomDefault(pageDimensions.width, pageDimensions.height);
        } else {
          const widthScale = containerWidth / pageDimensions.width;
          const roundedScale = Math.max(widthScale - 0.05, 0.1);
          setZoomLevel(roundedScale);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [pageDimensions, containerRef, defaultZoom]);

  const onPageLoadSuccess = (page) => {
    const { width, height } = page.getViewport({ scale: 1 });

    setPageSizes((prev) =>
      prev[page.pageNumber]?.width === width && prev[page.pageNumber]?.height === height
        ? prev
        : { ...prev, [page.pageNumber]: { width, height } }
    );

    // by whichever page happened to load last.
    if (baselineSized.current) return;
    baselineSized.current = true;

    setPageDimensions({
      width: width,
      height: height,
    });
    if (defaultZoom === "fit") {
      handleZoomDefault(width, height);
    }
  };

  const handleZoomDefault = (width, height) => {
    if (containerRef) {
      const containerWidth = containerRef?.offsetWidth || 0;
      const containerHeight = containerRef?.offsetHeight || 0;

      const fitScale = Math.min(containerWidth / width, containerHeight / height);
      setZoomLevel(Math.max(fitScale - 0.05, 0.1));
    }
  };

  const documentId = documentData?.id || documentData?.document_id;
  activeGeometryDocumentId.current = documentId;

  useEffect(() => {
    const query = debouncedSearchString.trim();
    if (!isVisible || !documentId || !numPages || !query) {
      setOcrSearchPages([]);
      setOcrSearchPagination({ truncated: false, nextAfterPage: null, isLoadingMore: false });
      setCompletedSearchQuery("");
      setSearchProgress({ done: 0, total: numPages || 0, isSearching: false });
      return;
    }

    const controller = new AbortController();
    setOcrSearchPages([]);
    setOcrSearchPagination({ truncated: false, nextAfterPage: null, isLoadingMore: false });
    setCompletedSearchQuery("");
    setSearchProgress({ done: 0, total: numPages, isSearching: true });

    const fetchFirstSearchWindow = async () => {
      const response = await api_without_cancellation.get(
        `/api/document/${documentId}/ocr-search/`,
        { params: { q: query }, signal: controller.signal }
      );
      const responseData = response.data?.data || response.data;
      const matchingPages = Array.isArray(responseData?.pages)
        ? responseData.pages
            .map((result) =>
              Number(typeof result === "object" && result !== null ? result.page : result)
            )
            .filter((pageNumber) => pageNumber >= 1 && pageNumber <= numPages)
        : [];

      setOcrSearchPages(matchingPages);
      setOcrSearchPagination({
        truncated: responseData?.truncated === true,
        nextAfterPage: responseData?.next_after_page ?? null,
        isLoadingMore: false,
      });
      setCompletedSearchQuery(query);
      setSearchProgress({
        done: matchingPages.length,
        total: Number(responseData?.total_match_pages) || matchingPages.length,
        isSearching: false,
      });

      if (matchingPages.length) scrollPageToView(matchingPages[0], setPage);
    };

    fetchFirstSearchWindow().catch((error) => {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Failed to search OCR document:", error);
      setCompletedSearchQuery(query);
      setSearchProgress({ done: 0, total: numPages, isSearching: false });
    });

    return () => controller.abort();
  }, [debouncedSearchString, documentId, isVisible, numPages]);

  useEffect(() => {
    const query = debouncedSearchString.trim();
    const lastLoadedMatch = ocrSearchPages[ocrSearchPages.length - 1];
    if (
      !query ||
      completedSearchQuery !== query ||
      !ocrSearchPagination.truncated ||
      ocrSearchPagination.nextAfterPage === null ||
      !lastLoadedMatch ||
      page < lastLoadedMatch
    ) {
      return;
    }

    const controller = new AbortController();
    const cursor = ocrSearchPagination.nextAfterPage;
    setSearchProgress((previous) => ({ ...previous, isSearching: true }));

    api_without_cancellation
      .get(`/api/document/${documentId}/ocr-search/`, {
        params: { q: query, after_page: cursor },
        signal: controller.signal,
      })
      .then((response) => {
        const responseData = response.data?.data || response.data;
        const nextPages = Array.isArray(responseData?.pages)
          ? responseData.pages
              .map((result) =>
                Number(typeof result === "object" && result !== null ? result.page : result)
              )
              .filter((pageNumber) => pageNumber >= 1 && pageNumber <= numPages)
          : [];

        setOcrSearchPages((previous) =>
          Array.from(new Set([...previous, ...nextPages])).sort((a, b) => a - b)
        );
        setOcrSearchPagination({
          truncated: responseData?.truncated === true,
          nextAfterPage: responseData?.next_after_page ?? null,
          isLoadingMore: false,
        });
        setSearchProgress((previous) => ({
          done: previous.done + nextPages.length,
          total: Number(responseData?.total_match_pages) || previous.total,
          isSearching: false,
        }));
      })
      .catch((error) => {
        if (error.name === "CanceledError" || error.name === "AbortError") return;
        console.error("Failed to load the next OCR search window:", error);
        setSearchProgress((previous) => ({ ...previous, isSearching: false }));
      });

    return () => controller.abort();
  }, [
    completedSearchQuery,
    debouncedSearchString,
    documentId,
    numPages,
    ocrSearchPages,
    ocrSearchPagination,
    page,
  ]);

  const getPageHighlights = (pageNumber = ocrSearchPages[0]) => {
    const query = debouncedSearchString.trim().toLowerCase();
    if (!query) return [];

    const geometry = ocrGeometryByPage[pageNumber] || {};
    const getNormalizedBox = (box) => {
      if (!box) return null;

      const left = Number(box.left);
      const top = Number(box.top);
      const width = Number(box.width);
      const height = Number(box.height);

      if (![left, top, width, height].every(Number.isFinite)) return null;
      if (left < 0 || top < 0 || width <= 0 || height <= 0 || left >= 1 || top >= 1) return null;

      // The API's coordinate_space is normalized_top_left. Clamp boxes at the
      // page edge so malformed OCR coordinates cannot overflow the PDF canvas.
      return {
        left,
        top,
        width: Math.min(width, 1 - left),
        height: Math.min(height, 1 - top),
      };
    };

    const lines = Array.isArray(geometry.lines) ? geometry.lines : [];
    return lines.flatMap((line, lineIndex) => {
      const lineText = String(line.text || "");
      const normalizedLineText = lineText.toLowerCase();
      const box = getNormalizedBox(line.bounding_box);
      if (!box || !lineText.length) return [];

      const matches = [];
      let startIndex = normalizedLineText.indexOf(query);

      // The API provides a box for the complete line, so locate the searched
      // substring proportionally inside it instead of highlighting the line.
      // Canvas measurement accounts for proportional characters (for example,
      // "i" is much narrower than "m"); raw character counts clipped the last
      // letters of matches such as "thera".
      if (!highlightMeasureContext.current && typeof document !== "undefined") {
        highlightMeasureContext.current = document.createElement("canvas").getContext("2d");
        if (highlightMeasureContext.current) {
          highlightMeasureContext.current.font = "100px Arial, sans-serif";
        }
      }
      const measureContext = highlightMeasureContext.current;
      const fullTextWidth = measureContext?.measureText(lineText).width || lineText.length;

      while (startIndex !== -1) {
        const prefixWidth =
          measureContext?.measureText(lineText.slice(0, startIndex)).width || startIndex;
        const matchWidth =
          measureContext?.measureText(lineText.slice(startIndex, startIndex + query.length))
            .width || query.length;
        const left = box.left + box.width * (prefixWidth / fullTextWidth);

        matches.push({
          left,
          top: box.top,
          width: Math.min(box.width * (matchWidth / fullTextWidth), box.left + box.width - left),
          height: box.height,
          key: `${pageNumber}-${line.index ?? lineIndex}-${startIndex}`,
        });
        startIndex = normalizedLineText.indexOf(query, startIndex + query.length);
      }

      return matches;
    });
  };

  const normalizedSearchString = searchString.trim();
  const isShowingSearchResults =
    Boolean(normalizedSearchString) &&
    normalizedSearchString === debouncedSearchString.trim() &&
    completedSearchQuery === debouncedSearchString.trim() &&
    !searchProgress.isSearching;
  const allPageNumbers = Array.from({ length: numPages || 0 }, (_, index) => index + 1);
  const thumbnailPageNumbers = isShowingSearchResults ? ocrSearchPages : allPageNumbers;

  // Pages close enough to the viewport to be worth rendering. Everything else
  // stays a sized placeholder, which is what stops pdf.js fetching the bytes
  // for the whole document.
  const visiblePages = usePdfPageWindow(containerRef, allPageNumbers.length);

  // Sizes placeholders for pages that have not rendered yet. Correct for the
  // uniform-page documents this app handles, and each page corrects itself the
  // first time it renders.
  const baselineSize = pageDimensions.width ? pageDimensions : null;

  useEffect(() => {
    if (!containerRef) return;

    const options = {
      root: containerRef,
      rootMargin: "0px",
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        const pageNumber = Number(entry.target.getAttribute("data-page-number"));
        pageVisibility.current.set(pageNumber, entry.isIntersecting ? entry.intersectionRatio : 0);
      });

      const mostVisiblePage = Array.from(pageVisibility.current.entries()).reduce(
        (best, current) => (current[1] > best[1] ? current : best),
        [null, 0]
      );
      if (mostVisiblePage[0] !== null) setPage(mostVisiblePage[0]);
    };

    observer.current = new IntersectionObserver(callback, options);

    // Scoped to this viewer's container: `.pdf-page` is also used by
    // AssembleDocumentModal and LibraryDocumentViewer, and a document-wide
    // query picked up their pages too.
    const pages = containerRef.querySelectorAll(".pdf-page");
    pages.forEach((page) => {
      observer.current.observe(page);
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
      pageVisibility.current.clear();
    };
  }, [containerRef, numPages]);
  useEffect(() => {
    const fetchDataFirstTime = async () => {
      try {
        setLoading(true);
        const response = await api.get("/api/doc-page/doc-page-rows/", {
          params: {
            page_id: "",
            case_id: Object.keys(documentData).length ? documentData?.for_case?.id : currentCaseId,
            client_id: Object.keys(documentData).length
              ? documentData?.for_client?.id
              : getClientId(),
          },
        });
        if (response.status === 200) {
          setSlotsData(response.data.data);
          setLoading(false);
        }
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };
    if (token) {
      fetchDataFirstTime();
    }
  }, [currentCaseId, documentData, token]);

  // useEffect(() => {
  //   const fetchDataFirstTime = async () => {
  //     try {
  //       setLoading(true);
  //       const response = await api.get("api/doc-page/list-page-doc-optimized/", {
  //         params: {
  //           page_id: "",
  //           case_id: Object.keys(documentData).length ? documentData?.for_case?.id : currentCaseId,
  //           all_docs: "True",
  //           client_id: Object.keys(documentData).length
  //             ? documentData?.for_client?.id
  //             : getClientId(),
  //         },
  //       });
  //       if (response.status === 200) {
  //         setSlotsData(response.data.data);
  //         setLoading(false);
  //       }
  //     } catch (error) {
  //       console.error(error);
  //       setLoading(false);
  //     }
  //   };
  //   if (token) {
  //     fetchDataFirstTime();
  //   }
  // }, [currentCaseId, documentData, token]);

  const fetchPageDetails = async (caseId) => {
    try {
      const response = await api_without_cancellation.get(`/api/pages/${caseId}/`);

      setPages(response.data);
    } catch (error) {
      console.error("Error occurred while fetching pages", error);
    }
  };

  useEffect(() => {
    setPages([]);
    if (documentData?.for_case?.id) {
      fetchPageDetails(documentData?.for_case?.id);
    }
  }, [documentData]);

  const abortControllerRef = useRef(null);

  const refetchSlotsData = async () => {
    if (documentData?.for_case?.id && documentData?.for_client?.id) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await api_without_cancellation.get("/api/doc-page/doc-page-rows/", {
          params: {
            page_id: "",
            case_id: documentData?.for_case?.id || "",
            client_id: documentData?.for_client?.id || "",
          },
          signal: controller.signal,
        });

        if (response.status === 200) {
          setSlotsData(response.data.data);
        }
      } catch (error) {
        if (error.name === "CanceledError") {
          console.error("Previous API call aborted.");
        } else {
          console.error("API error:", error);
        }
      }
    }
  };

  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);

  useEffect(() => {
    const query = debouncedSearchString.trim();
    if (
      !isVisible ||
      !documentId ||
      !numPages ||
      !query ||
      completedSearchQuery !== query ||
      !ocrSearchPages.length
    ) {
      return;
    }

    const fetchGeo = async () => {
      const pagesToFetch = ocrSearchPages.filter(
        (matchPage) =>
          Math.abs(matchPage - page) <= 2 &&
          !ocrGeometryByPage[matchPage] &&
          !fetchingOcrPages.current.has(matchPage)
      );
      if (!pagesToFetch.length) return;
      pagesToFetch.forEach((pageNumber) => fetchingOcrPages.current.add(pageNumber));

      try {
        const response = await api_without_cancellation.get(
          `/api/document/${documentId}/ocr-geometry/`,
          { params: { pages: pagesToFetch.join(",") } }
        );

        if (activeGeometryDocumentId.current !== documentId) return;

        const responseData = response.data?.data || response.data;
        const geometries = Array.isArray(responseData)
          ? responseData
          : responseData?.pages || responseData?.results || responseData?.geometries || [];

        setOcrGeometryByPage((previous) => {
          const next = { ...previous };
          pagesToFetch.forEach((pageNumber) => {
            next[pageNumber] = { lines: [] };
          });
          geometries.forEach((geometry) => {
            const pageNumber = Number(geometry.page ?? geometry.page_number ?? geometry.pageNumber);
            if (Number.isFinite(pageNumber)) next[pageNumber] = geometry;
          });
          return next;
        });
      } catch (error) {
        console.error("Failed to fetch OCR geometry:", error);
      } finally {
        pagesToFetch.forEach((pageNumber) => fetchingOcrPages.current.delete(pageNumber));
      }
    };

    fetchGeo();
  }, [
    completedSearchQuery,
    debouncedSearchString,
    documentId,
    isVisible,
    numPages,
    ocrGeometryByPage,
    ocrSearchPages,
    page,
  ]);
  const fileExtension =
    (documentData?.upload || documentURL || "").split(/[?#]/)[0].match(/\.[a-z0-9]+$/i)?.[0] ||
    ".pdf";
  const documentName = documentData?.file_name || "Untitled Document";
  const viewerFileName = documentName.toLowerCase().endsWith(fileExtension.toLowerCase())
    ? documentName
    : `${documentName}${fileExtension}`;

  return (
    <>
      {!deleteConfirmModal && isVisible && (
        <Modal
          show={isVisible}
          onHide={handleHideDocumentModal}
          size="xl"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          className="universal-document-modal custom-modal-width"
          dialogClassName="document-modal-padding-setting"
        >
          <Modal.Header
            className="document-modal-header d-flex align-items-center justify-content-center height-25"
            style={{ borderRadius: "0px", borderBottom: "none" }}
          >
            <Modal.Title
              id="contained-modal-title-vcenter"
              style={{
                fontSize: "14px",
                fontWeight: "600",
                textTransform: "none",
                minWidth: 0,
                maxWidth: "100%",
              }}
              className="d-flex align-items-center justify-content-center height-25"
            >
              <span className="text-truncate" title={`Doc Viewer: ${viewerFileName}`}>
                Doc Viewer: {viewerFileName}
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="document-modal-body">
            <div
              className="document-viewer-shell d-flex flex-column justify-content-between m-0"
              style={{
                width: "calc(100%)",
                minWidth: 0,
              }}
            >
              <div
                className="document-viewer-content d-flex"
                style={{
                  width: "100%",
                  minWidth: 0,
                }}
              >
                {showSideBar && (
                  <PdfThumbnailRail scrollRef={setRailScrollEl} portalRef={setRailPortalEl} />
                )}
                <div className="document-viewer-center d-flex flex-column">
                  <div className="search-and-error-area">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {pages.length === 0 && <DocumentReviewTo />}
                    <div className="document-viewer-control-row m-l-5 m-r-5 m-t-5 m-b-5">
                      <label className="document-viewer-control-label" htmlFor={documentSearchId}>
                        Search in Document:
                      </label>
                      <input
                        id={documentSearchId}
                        name="documentSearch"
                        type="text"
                        autoComplete="off"
                        className="form-control document-viewer-term-search document-search-text-color py-0 height-25"
                        placeholder="Type Search Terms to Only Display Pages With Those Terms"
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                      />
                    </div>
                  </div>

                  <div
                    className=" main-doc-area m-0"
                    style={{
                      width: "100%",
                      backgroundColor: "var(--primary-20)",
                      overflowY: "scroll",
                      overflowX: "auto",
                      scrollbarWidth: "none",
                    }}
                    ref={setContainerRef}
                  >
                    <Document
                      file={source !== "chat" ? documentURL : cleanUrl(documentURL)}
                      options={PDF_LOAD_OPTIONS}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) => {
                        console.error(error);
                        setError("Failed to load this file");
                      }}
                    >
                      {/* Thumbnails render as children of this <Document> so they
                          share its single load, but are portaled into the rail so
                          the layout is unchanged. Portals preserve React context,
                          so <Page> still finds react-pdf's DocumentContext. */}
                      {showSideBar &&
                        railPortalEl &&
                        createPortal(
                          <PdfThumbnailPages
                            numPages={numPages}
                            pageNumbers={thumbnailPageNumbers}
                            currentPage={page}
                            scrollRootEl={railScrollEl}
                            pageSizes={pageSizes}
                            baselineSize={baselineSize}
                            onThumbnailClick={scrollPageToView}
                            setPageNumber={setPage}
                          />,
                          railPortalEl
                        )}

                      {allPageNumbers.map((pageNumber) => {
                        const size = pageSizes[pageNumber] || baselineSize;

                        return (
                          <div
                            key={pageNumber}
                            id={`pdf_page_${pageNumber}`}
                            data-page-number={pageNumber}
                            data-window-key={pageNumber}
                            className="pdf-page"
                            style={{
                              ...pdfPageStyle,
                              width: size ? size.width * zoomLevel : "min-content",
                              height: size ? size.height * zoomLevel : undefined,
                            }}
                          >
                            {visiblePages.has(pageNumber) && (
                              <>
                                <Page
                                  scale={zoomLevel}
                                  pageNumber={pageNumber}
                                  renderTextLayer={false}
                                  renderAnnotationLayer={false}
                                  onLoadSuccess={onPageLoadSuccess}
                                  className="pdf-page-content"
                                  style={{
                                    transition: "transform 0.2s ease",
                                    transformOrigin: "center center",
                                  }}
                                />
                                <div
                                  aria-hidden="true"
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    pointerEvents: "none",
                                    zIndex: 2,
                                  }}
                                >
                                  {getPageHighlights(pageNumber).map((highlight) => (
                                    <span
                                      key={highlight.key}
                                      style={{
                                        position: "absolute",
                                        left: `calc(${highlight.left * 100}% - 2.5px)`,
                                        top: `calc(${highlight.top * 100}% - 2.5px)`,
                                        width: `calc(${highlight.width * 100}% + 5px)`,
                                        height: `calc(${highlight.height * 100}% + 5px)`,
                                        background: "rgba(255, 221, 0, 0.45)",
                                        outline: "1px solid rgba(220, 180, 0, 0.8)",
                                        opacity: 0.5,
                                      }}
                                    />
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </Document>
                  </div>
                  <div className="document-viewer-case-search">
                    <ClientSearch
                      setError={setError}
                      fetchPageDetails={fetchPageDetails}
                      setLoading={setLoading}
                      showAttachButton={pages.length === 0}
                    />
                  </div>
                </div>
                <DocumentModalSideBar
                  source={source}
                  documentData={documentData}
                  pages={pages}
                  slotsData={slotsData}
                  onRefetchSlotsData={refetchSlotsData}
                  loading={loading}
                  setSlotsData={setSlotsData}
                  setDeleteConfirmModal={setDeleteConfirmModal}
                  refetchDocumentData={refetchDocumentData}
                  documentURL={documentURL}
                  firms={firms}
                  selectedFirm={selectedFirm}
                  users={users}
                  chatLoading={chatLoading}
                  chatError={chatError}
                  handleSelectFirm={handleSelectFirm}
                  sendMessage={sendMessage}
                  searchUsers={searchUsers}
                  setChatDocumentThread={setChatDocumentThread}
                  setUpdatedUser={setUpdatedUser}
                  handleSelectUser={handleSelectUser}
                  hideDocumentModal2={handleHideDocumentModal}
                  isDisabled={pages?.length === 0}
                />
              </div>
              <PdfNavigation
                alignWithDocument
                documentViewport={containerRef}
                showSearch={false}
                totalPages={numPages}
                pageNumber={page}
                setPageNumber={setPage}
                pageWidth={pageDimensions.width}
                onZoomChange={onZoomChange}
                zoomScale={zoomLevel}
                scrollPageToView={scrollPageToView}
                isChatOpen={(threadId && updatedUser) || (chatDocumentThread && updatedUser)}
              />
            </div>

            {threadId && updatedUser && !chatDocumentThread && (
              <div
                style={{
                  minWidth: "200px",
                  flex: "0 0 30%",
                }}
              >
                <ChatWindow
                  user={updatedUser}
                  messages={messages}
                  typingUsers={typingUsers}
                  sendMessage={sendMessage}
                  handleSendMessage={(text) => sendMessage(updatedUser?.id, text)}
                  handleTyping={handleTyping}
                  markAsRead={markAsRead}
                  loadMoreMessages={loadMoreMessages}
                  source={source}
                />
              </div>
            )}
            {chatDocumentThread && updatedUser && (
              <div
                style={{
                  minWidth: "200px",
                  flex: "0 0 30%",
                }}
              >
                <ChatWindow
                  user={updatedUser}
                  messages={messages}
                  typingUsers={typingUsers}
                  sendMessage={sendMessage}
                  handleSendMessage={(text) => sendMessage(updatedUser?.id, text)}
                  handleTyping={handleTyping}
                  markAsRead={markAsRead}
                  loadMoreMessages={loadMoreMessages}
                  source={"chat"}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer
            style={{
              margin: 0,
              padding: 0,
              border: 0,
            }}
          />
        </Modal>
      )}

      {deleteConfirmModal && (
        <DeleteConfirmationModal
          show={deleteConfirmModal}
          handleClose={() => setDeleteConfirmModal(false)}
          // onComplete={handleDeleteComplete}
        />
      )}
    </>
  );
};

export default DocumentModal;
